import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Check, X, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

export default function Leaves() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ leave_type: "annual", start_date: "", end_date: "", reason: "" });

  const reload = async () => { const r = await api.get("/leaves"); setItems(r.data); };
  useEffect(() => { reload(); }, []);

  const submit = async () => {
    if (!form.start_date || !form.end_date) return toast.error("Dates are required");
    try {
      await api.post("/leaves", form);
      toast.success("Leave request submitted");
      setOpen(false); setForm({ leave_type: "annual", start_date: "", end_date: "", reason: "" }); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const decide = async (id, status) => {
    try {
      await api.post(`/leaves/${id}/decision`, { status });
      toast.success(`Leave ${status}`); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this leave request? This cannot be undone.")) return;
    try {
      await api.delete(`/leaves/${id}`);
      toast.success("Leave deleted"); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const badge = (s) => s === "approved" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
    : s === "rejected" ? "bg-red-500/10 text-red-400 border-red-500/30"
    : "bg-amber-500/10 text-amber-400 border-amber-500/30";

  return (
    <div className="space-y-6" data-testid="leaves-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">LEAVE</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight mt-1">Time Off</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-leave-btn" className="rounded-none btn-brand border border-[#3b2bd0] font-heading uppercase">
              <Plus className="h-4 w-4 mr-2" /> Request Leave
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0a0a0a] border-[#222] rounded-none text-white">
            <DialogHeader><DialogTitle className="font-heading uppercase">New Leave Request</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="label-eyebrow">TYPE</Label>
                <Select value={form.leave_type} onValueChange={(v) => setForm({ ...form, leave_type: v })}>
                  <SelectTrigger className="bg-black border-[#222] rounded-none mt-2 h-10 font-mono"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-[#222] rounded-none">
                    <SelectItem value="annual">Annual Leave</SelectItem>
                    <SelectItem value="sick">Sick Leave</SelectItem>
                    <SelectItem value="unavailable">Unavailable</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="label-eyebrow">FROM</Label><div className="mt-2"><DatePicker value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} testid="leave-from" /></div></div>
                <div><Label className="label-eyebrow">TO</Label><div className="mt-2"><DatePicker value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} testid="leave-to" /></div></div>
              </div>
              <div>
                <Label className="label-eyebrow">REASON</Label>
                <Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="bg-black border-[#222] rounded-none mt-2 font-mono text-sm" rows={3} />
              </div>
            </div>
            <DialogFooter><Button onClick={submit} data-testid="submit-leave-btn" className="rounded-none btn-brand border border-[#3b2bd0] font-heading uppercase">Submit</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="rounded-none bg-[#0a0a0a] border-[#1c1c1c] overflow-x-auto">
        <table className="w-full text-sm" data-testid="leaves-table">
          <thead>
            <tr className="text-[#666] label-eyebrow border-b border-[#1c1c1c]">
              <Th>EMPLOYEE</Th><Th>TYPE</Th><Th>FROM</Th><Th>TO</Th><Th align="right">DAYS</Th><Th>STATUS</Th><Th>REASON</Th><Th>ACTION</Th>
            </tr>
          </thead>
          <tbody>
            {items.map((l) => {
              const canDelete = user.role === "admin" || (l.user_id === user.id && l.status === "pending");
              return (
              <tr key={l.id} className="border-t border-[#1c1c1c] hover:bg-[#101010]">
                <Td>{l.user_name}</Td>
                <Td><span className="label-eyebrow">{l.leave_type}</span></Td>
                <Td mono>{l.start_date}</Td>
                <Td mono>{l.end_date}</Td>
                <Td mono align="right">{l.days}</Td>
                <Td><Badge variant="outline" className={`rounded-none font-mono text-xs uppercase ${badge(l.status)}`}>{l.status}</Badge></Td>
                <Td><span className="text-[#aaa] line-clamp-1">{l.reason || "—"}</span></Td>
                <Td>
                  <div className="flex gap-1 items-center">
                    {user.role === "admin" && l.status === "pending" && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => decide(l.id, "approved")} data-testid={`approve-${l.id}`} className="rounded-none h-7 px-2 text-emerald-400" title="Approve"><Check className="h-3 w-3" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => decide(l.id, "rejected")} className="rounded-none h-7 px-2 text-red-400" title="Reject"><X className="h-3 w-3" /></Button>
                      </>
                    )}
                    {canDelete && (
                      <Button size="sm" variant="ghost" onClick={() => remove(l.id)} data-testid={`delete-leave-${l.id}`} className="rounded-none h-7 px-2 text-red-400" title="Delete">
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                    {!canDelete && user.role !== "admin" && <span className="font-mono text-xs text-[#666]">—</span>}
                  </div>
                </Td>
              </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan={8} className="text-center py-10 font-mono text-[#666]">NO LEAVE REQUESTS</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
const Th = ({ children, align }) => <th className={`px-4 py-3 font-normal ${align === "right" ? "text-right" : "text-left"}`}>{children}</th>;
const Td = ({ children, mono, align }) => <td className={`px-4 py-3 ${mono ? "font-mono tabular" : ""} ${align === "right" ? "text-right" : ""}`}>{children}</td>;
